/**
 * RegionalSupplies — bucket 4 of 6. Renders the design's four-module
 * Inventory & Resupply layout: Projected Inventory, May Inventory Audit,
 * Next Resupply, Upcoming Resupplies.
 *
 * Every module depends on the inventory schema, which doesn't exist yet
 * (no op-DB or warehouse source). The whole surface therefore renders the
 * design layout as an em-dash skeleton behind a single <MockedOverlay>,
 * mirroring RegionalCorp. It lights up when BON-151 lands.
 */
import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, MockedOverlay } from '../primitives';

import { BucketError, BucketLoading } from './BucketStates';
import type { RegionalBucketProps } from './regional-bucket';
import { useRegionalSupplies } from './regionalApi';
import { AuditCard } from './supplies/AuditCard';
import { NextResupplyCard } from './supplies/NextResupplyCard';
import { ProjectedInventoryCard } from './supplies/ProjectedInventoryCard';
import { UpcomingResuppliesCard } from './supplies/UpcomingResuppliesCard';

export function RegionalSupplies({ regionSlug }: RegionalBucketProps) {
  const q = useRegionalSupplies(regionSlug);

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <MockedOverlay reason="Inventory & resupply data isn’t wired up yet. Goes live once the inventory module ships — tracked in BON-151.">
      <KpiStrip items={adaptKpis(data.kpis)} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
          gap: 14,
          marginTop: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <ProjectedInventoryCard />
          <AuditCard />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <NextResupplyCard />
          <UpcomingResuppliesCard />
        </div>
      </div>
    </MockedOverlay>
  );
}
